/**
 * Madrid's CLAVEL — the carnation — in the shared 120×120 LINE asset box.
 *
 * The Home tab, all four passcode dots and the chat wallpaper draw it.
 *
 * WHY THE CARNATION, AND NOT A ROSE
 *
 * The owner asked whether a rose is Madrid's symbol or flamenco's. It is neither,
 * really: the rose belongs to the generic Spanish-dancer picture, and it is also
 * England's, Portugal's and half a dozen other places' — a flower that belongs to
 * everyone identifies nobody. The CLAVEL is Madrid's own. It is what a chulapa
 * wears in her hair at San Isidro, it is the flower of the city's own festival
 * dress, and it is the one a flamenca traditionally wears too.
 *
 * It is also the better drawing, which matters more here than the provenance. A
 * rose is a spiral of overlapping curls; shrink it and the curls close into a
 * whorl that reads as a shell or a fingerprint. A carnation is a FRINGED disc on a
 * TUBE, and both of those survive.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE FRINGE. A carnation's petals are serrated, and the notched outline is the
 *    whole identification — smooth it and the flower becomes a pompom, a cloud or
 *    a dahlia. The notches are cut deep for that reason: shallow ones vanish first.
 *  - THE CALYX IS A TUBE, narrow and straight-sided, and it is long. Every other
 *    flower in this family sits straight on its stem; this one sits on a green
 *    cylinder, and that cylinder is what separates it from Spring's tulip and
 *    Sakura's blossom at a glance.
 *  - THE HEAD IS WIDER THAN IT IS TALL. A carnation opens out flat. Drawn as tall
 *    as it is wide it reads as a bud, which is the one thing this flower never
 *    looks like.
 *  - IT LEANS. The head sits slightly off the stem's axis, because a real clavel
 *    is too heavy for its own tube and always tips.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/**
 * A polygon in a NORMALISED winding order, whichever order it arrives in.
 *
 * Every closed shape here goes through this. A hand-authored subpath wound the
 * other way is not a shape under a nonzero fill — it is a hole punched through
 * whatever it overlaps, which is how Spring's bellflower lost its leaf.
 */
function poly( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/**
 * Catmull-Rom through the points, sampled back out as points.
 *
 * Used on the fringe. Walking tip-notch-tip in straight lines gives real POINTS,
 * and a carnation's petal edge is soft — it is torn paper, not a saw. Running the
 * same points through a curve keeps every scallop and takes the needle off each
 * one, which is the difference between a flower and a cog.
 */
function smooth( pts: [ number, number ][], per = 5 ): [ number, number ][] {
  const n = pts.length, out: [ number, number ][] = [];
  for ( let i = 0; i < n; i++ ) {
    const p0 = pts[ ( i - 1 + n ) % n ], p1 = pts[ i ];
    const p2 = pts[ ( i + 1 ) % n ], p3 = pts[ ( i + 2 ) % n ];
    for ( let s = 0; s < per; s++ ) {
      const t = s / per, t2 = t * t, t3 = t2 * t;
      out.push( [
        0.5 * ( 2 * p1[ 0 ] + ( -p0[ 0 ] + p2[ 0 ] ) * t
          + ( 2 * p0[ 0 ] - 5 * p1[ 0 ] + 4 * p2[ 0 ] - p3[ 0 ] ) * t2
          + ( -p0[ 0 ] + 3 * p1[ 0 ] - 3 * p2[ 0 ] + p3[ 0 ] ) * t3 ),
        0.5 * ( 2 * p1[ 1 ] + ( -p0[ 1 ] + p2[ 1 ] ) * t
          + ( 2 * p0[ 1 ] - 5 * p1[ 1 ] + 4 * p2[ 1 ] - p3[ 1 ] ) * t2
          + ( -p0[ 1 ] + 3 * p1[ 1 ] - 3 * p2[ 1 ] + p3[ 1 ] ) * t3 ),
      ] );
    }
  }
  return out;
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;
/** Where the head's centre sits, and how far it leans off the stem. */
const HEAD_CY = 46, LEAN = 5;
/** The head is WIDER than tall — a clavel opens out flat. */
const RX = 34, RY = 26;

/**
 * THE HEAD — a fringed disc.
 *
 * Built by walking the ellipse and stepping alternately out to a petal tip and in
 * to the notch between two petals. THIRTEEN tips at 0.83 of the radius.
 *
 * The first pass used nine at 0.72 and came back as a STAR — a carnation's edge is
 * finely serrated, not spiked, and deep notches on few tips is the recipe for a
 * sun or a gear. The count went up and the notches got shallower together: more
 * teeth need less depth each to read as a fringe, and thirteen shallow ones is a
 * ruffle where nine deep ones was a rosette.
 */
export const CLAVEL_HEAD = ( () => {
  const TIPS = 12, NOTCH = 0.845;
  const pts: [ number, number ][] = [];
  for ( let i = 0; i < TIPS * 2; i++ ) {
    const a = ( i / ( TIPS * 2 ) ) * Math.PI * 2 - Math.PI / 2;
    // the tips are not all the same length: a real head is ragged, and a perfectly
    // even fringe reads as a machined part
    const jitter = 1 + ( ( i * 7 ) % 5 ) * 0.022;
    const k = i % 2 === 0 ? jitter : NOTCH;
    pts.push( [ CX + LEAN + Math.cos( a ) * RX * k, HEAD_CY + Math.sin( a ) * RY * k ] );
  }
  // SMOOTHED, at seven samples per span.
  //
  // Three numbers move together here and it is worth writing down which way. More
  // SAMPLES round each tip. Fewer TEETH give every scallop more arc to be a curve
  // across, so it kinks less. A shallower NOTCH lowers the whole amplitude. All
  // three soften, and the trap is that pushing only the notch flattens the fringe
  // into a circle before it ever stops looking sharp — the points come from the
  // corners, not from the depth.
  return poly( smooth( pts, 7 ) );
} )();

/**
 * The INNER fringe — a second, smaller ring of tips, turned half a step so it
 * shows through the gaps in the first.
 *
 * This is the one piece of modelling the flower gets. Flat, a carnation is a
 * cog; with a second ring behind the first it reads as a head with depth, which is
 * what the passcode dot has the room to show.
 */
export const CLAVEL_INNER = ( () => {
  const TIPS = 8, NOTCH = 0.82;
  const pts: [ number, number ][] = [];
  for ( let i = 0; i < TIPS * 2; i++ ) {
    const a = ( i / ( TIPS * 2 ) ) * Math.PI * 2 - Math.PI / 2 + Math.PI / TIPS;
    const k = i % 2 === 0 ? 1 : NOTCH;
    pts.push( [ CX + LEAN + Math.cos( a ) * RX * 0.58 * k, HEAD_CY + Math.sin( a ) * RY * 0.58 * k ] );
  }
  return poly( smooth( pts, 4 ) );
} )();

/**
 * THE CALYX — the tube the head sits in, and the mark that says carnation rather
 * than "flower".
 *
 * Straight-sided and long: it is nearly a third of the plant. Tapered or short it
 * turns into an ordinary receptacle and the flower joins the generic pile.
 */
export const CLAVEL_CALYX = poly( [
  [ CX - 8, HEAD_CY + 12 ],
  [ CX - 7, HEAD_CY + 36 ],
  [ CX + 7, HEAD_CY + 36 ],
  [ CX + 8, HEAD_CY + 12 ],
] );

/** THE STEM: straight, and exactly centred under the calyx. */
export const CLAVEL_STEM = poly( [
  [ CX - 3.2, HEAD_CY + 34 ], [ CX - 3.2, 112 ], [ CX + 3.2, 112 ], [ CX + 3.2, HEAD_CY + 34 ],
] );

/**
 * The two LEAVES — narrow, sharp and steeply upright.
 *
 * A carnation's leaves are grass-like, nothing like a tulip's broad blades, and
 * that difference is worth the few pixels it costs: the two flowers in this
 * family are otherwise a red one and a purple one.
 */
export const CLAVEL_LEAVES = ( () => {
  const blade = ( dir: number ): string => {
    const pts: [ number, number ][] = [];
    const N = 20, LEN = 27, W = 7.0;
    // The tip is ROUNDED, not pointed. The first pass ran the width to zero at the
    // end and the pair came out as two needles standing either side of the stem;
    // a carnation's leaf is narrow but it is a blade of grass, and a blade of grass
    // ends in a curve you can see at this size.
    const width = ( t: number ): number => W * Math.sin( Math.pow( t, 0.5 ) * Math.PI * 0.78 + 0.24 );
    for ( let i = 0; i <= N; i++ ) {
      const t = i / N;
      pts.push( [ CX + dir * ( 2 + t * 15 ), 108 - t * LEN - width( t ) * 0.35 ] );
    }
    for ( let i = N; i >= 0; i-- ) {
      const t = i / N;
      pts.push( [ CX + dir * ( 2 + t * 15 ), 108 - t * LEN + width( t ) ] );
    }
    return poly( smooth( pts, 3 ) );
  };
  return `${ blade( -1 ) } ${ blade( 1 ) }`;
} )();

/** Everything green: calyx, stem and both leaves, as ONE path.
 *
 *  One path rather than four matters wherever the fill is translucent: fill-opacity
 *  applies to the path and not to each subpath, so the leaves may cross the stem
 *  without the crossing going darker. */
export const CLAVEL_FOLIAGE = `${ CLAVEL_LEAVES } ${ CLAVEL_STEM } ${ CLAVEL_CALYX }`;

/** THE WHOLE FLOWER as one path — head, calyx, stem and leaves. This is what the
 *  idle tab and the unentered passcode dot draw. */
export const CLAVEL = `${ CLAVEL_FOLIAGE } ${ CLAVEL_HEAD }`;

/**
 * The green every clavel's foliage takes, whatever red its head is.
 *
 * FIXED, for the reason Spring's tulips settled: a bunch of carnations is several
 * flower shades on ONE kind of plant, and letting the leaves follow the bloom made
 * four different species out of four digits.
 */
export const CLAVEL_GREEN = '#4A7A32';

/**
 * A SIMPLER flower for the wallpaper's smaller grains: the head alone, on a short
 * calyx, with no leaves and a coarser fringe.
 *
 * Same reason every module here has one. At a third of this box the leaves are
 * under a pixel wide and thirteen notches close into a circle; nine slightly
 * deeper ones keep the fringe that identifies the flower.
 */
export const CLAVEL_SIMPLE = ( () => {
  const TIPS = 8, NOTCH = 0.83;
  const pts: [ number, number ][] = [];
  for ( let i = 0; i < TIPS * 2; i++ ) {
    const a = ( i / ( TIPS * 2 ) ) * Math.PI * 2 - Math.PI / 2;
    const k = i % 2 === 0 ? 1 : NOTCH;
    pts.push( [ CX + Math.cos( a ) * RX * k, HEAD_CY + Math.sin( a ) * RY * k ] );
  }
  return `${ poly( smooth( pts, 4 ) ) } ${ poly( [
    [ CX - 7, HEAD_CY + 14 ], [ CX - 6, HEAD_CY + 34 ],
    [ CX + 6, HEAD_CY + 34 ], [ CX + 7, HEAD_CY + 14 ],
  ] ) }`;
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read them,
 * and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered carnations, on the lock screen's cream upper zone (#FEF9E8,
 * sampled from the owner's own shot).
 *
 * ALL FOUR ARE RED, which is the constraint the owner set for this theme and a
 * tighter one than anything else in the family has had: every other ramp walks
 * across a material — honey to rosewood, wet slate to driftwood — and this one has
 * to stay in one hue and still separate four times.
 *
 * So it walks the red the way a bunch of claveles actually does, from the pale
 * pink of a half-open one to the near-black of a full-blown one in shadow.
 * Measured on the cream: 2.74 / 4.33 / 6.31 / 9.37, monotonic, each step about
 * 1.5x the last — a wider spread than usual, which is what a single hue needs to
 * stay countable.
 */
export const CLAVEL_PC_COLORS = [ '#E8776C', '#D0453C', '#A8322C', '#7A2622' ];

/**
 * ONE tone for every unentered carnation — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.54:1 on the cream. The lit ones run 2.74 to 9.37, so an entered digit outweighs
 * an empty one by nearly two to six times.
 */
export const CLAVEL_PC_OFF = '#E3C79A';

/**
 * A per-digit lean, alternating side, about the foot of the stem.
 *
 * The flower already leans on its own tube, so this stays small — leaning a lean
 * reads as a picked flower rather than a growing one.
 */
export const CLAVEL_PC_TILTS = [ -6, 5, -3, 7 ];
